<template>
  <div>
    <!-- Subcontractor Header -->
    <div
      class="sticky top-0 p-6 flex items-center justify-between hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer"
      @click="expanded = !expanded"
    >
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center space-x-3">
          <button
            type="button"
            class="btn-icon btn-icon-default"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 transition-transform duration-200"
              :class="expanded ? 'rotate-90' : ''">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
          </button>

          <h3 class="text-lg font-semibold text-gray-900">
            {{ subcontractor.name }}
          </h3>
        </div>

        <div class="text-right">
          <div class="text-sm font-semibold text-gray-900">
            Adjusted Awarded Value <span class="font-normal">{{ $tcc(totalAdjustedValue) }}</span>
          </div>

          <div
            v-if="$props.projectSupplier.contract_value"
            class="text-sm font-semibold text-gray-900"
          >
            Contract Value <span class="font-normal">{{ $tcc($props.projectSupplier.contract_value) }}</span>
          </div>

          <button
            v-else-if="projectsStore.project?.unarchived"
            class="btn btn--tertiary-blue p-0"
            type="button"
            @click.stop="openSupplierModal()"
          >
            Set Contract Value
          </button>
        </div>
      </div>
    </div>

    <!-- Packages Table -->
    <div
      v-if="expanded"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider w-96">
                Package
              </th>

              <th class="px-4 py-3 text-left text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider">
                Awarded Value
              </th>

              <th class="px-4 py-3 text-left text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider">
                Approved Variation
              </th>

              <th class="px-4 py-3 text-left text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider">
                Adjusted Value
              </th>

              <th class="px-4 py-3 text-left text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider">
                Estimated Value
              </th>

              <th class="px-4 py-3 text-left text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider">
                Variation Orders
              </th>

              <th class="px-4 py-3 text-right text-xs xl:text-xxs font-semibold text-gray-600 uppercase tracking-wider" />
            </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-100">
            <PackageVariationRow
              v-for="subcontractorPackage in subcontractor.packages"
              :key="subcontractorPackage.id"
              :subcontractor-package="subcontractorPackage"
              :project-supplier="projectSupplier"
              :package-summary="getPackageSummary(subcontractorPackage.id)"
              class="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
            />
          </tbody>
        </table>
      </div>
    </div>

    <ProjectSupplierModal
      v-if="showSupplierModal"
      :project-supplier="selectedProjectSupplier"
      :project-id="projectsStore.project.id"
      @close="showSupplierModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import ProjectSupplierModal from '@/components/ProjectSettings/VendorsTab/ProjectSupplierModal.vue';
import { australianCurrency, tcc } from '@/helpers';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import Supplier from '@/models/Supplier';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { computed, ref } from 'vue';
import PackageVariationRow from './PackageVariationRow.vue';

const props = defineProps<{
  subcontractor: Supplier;
  projectSupplier: ProjectSupplier;
  summary?: SubcontractVariationOrderDashboard['summary'];
}>();

const expanded = ref(false);
const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();

const getPackageSummary = (packageId: number) => {
  if(!props.summary) {
    return undefined;
  }

  return props.summary.find((summary) => summary.package_id === packageId);
};

const totalAdjustedValue = computed(() => {
  if(!props.subcontractor.packages || !props.summary) {
    return 0;
  }

  return props.subcontractor.packages.reduce((total, pkg) => {
    const packageSummary = getPackageSummary(pkg.id);
    const awardedValue = pkg.totalAwarded || 0;
    const approvedAmount = packageSummary?.approved_pricing || 0;
    const adjustedValue = awardedValue + approvedAmount;

    return total + adjustedValue;
  }, 0);
});

const showSupplierModal = ref(false);
const selectedProjectSupplier = ref(undefined);

const openSupplierModal = () => {
  selectedProjectSupplier.value = props.projectSupplier;
  showSupplierModal.value = true;
};
</script>

<style scoped></style>
